import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellDot, CheckCheck, Check, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function TaskCompletionAlerts({ notifications, alertsEnabled }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = notifications?.items || [];
  const count = alertsEnabled ? Number(notifications?.count) || 0 : 0;
  const read = async (entry, visit = false) => {
    setBusy(true);
    try {
      await api.put(entry ? `/notifications/${entry.id}/read` : "/notifications/completion-alerts/read-all");
      window.dispatchEvent(new Event("whitenton:tasks-changed"));
      if (visit) {
        setOpen(false);
        navigate(entry.path?.startsWith("/tasks?") ? entry.path : "/tasks?filter=completed");
      }
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" title="Completed assignments" aria-label="Completed assignments" className="relative h-9 w-9 shrink-0 sm:h-10 sm:w-10" data-testid="completion-alerts-button">
          <BellDot className="h-5 w-5 text-[#647069]" />
          {count > 0 && <span className="absolute -right-1 -top-1 rounded-full bg-[#B85C42] px-1 text-[10px] text-white" data-testid="completion-alerts-count">{count > 99 ? "99+" : count}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(380px,calc(100vw-24px))] p-0" data-testid="completion-alerts-menu">
        <div className="flex items-center justify-between gap-2 border-b p-3">
          <h2 className="text-sm font-semibold">Completed assignments</h2>
          {!!items.length && <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={busy} onClick={() => read()} title="Mark all read" aria-label="Mark all completion alerts read"><CheckCheck className="h-4 w-4" /></Button>}
        </div>
        <div className="max-h-[min(420px,65vh)] overflow-y-auto">
          {!items.length && <p className="p-4 text-sm text-muted-foreground">No new completion notifications.</p>}
          {items.map((entry) => <div key={entry.id} className="flex min-w-0 items-start gap-1 border-b p-2 last:border-0" data-testid={`completion-alert-${entry.id}`}>
            <button type="button" className="min-w-0 flex-1 rounded p-2 text-left hover:bg-muted/40" disabled={busy} onClick={() => read(entry, true)}>
              <p className="whitespace-pre-wrap break-words text-sm [overflow-wrap:anywhere]">{entry.message}</p>
              <time dateTime={entry.created_at} className="mt-1 block text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleString()}</time>
            </button>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={busy} onClick={() => read(entry)} title="Mark read" aria-label="Mark completion notification read">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</Button>
          </div>)}
        </div>
      </PopoverContent>
    </Popover>
  );
}
